import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import Switch from '@mui/material/Switch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EmailIcon from '@mui/icons-material/Email';
import BuildIcon from '@mui/icons-material/Build';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTheme } from '@mui/material/styles';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PATCH, GET } from '../../../../configs/api';
import { api } from '../../../../utils/constants';
import { addDaysToWallClockDate, formatWallClockDate } from '../../../../utils/invoiceDateTime';
import { useSelector } from 'react-redux';
import { getSelectedCompanyName } from '../../../../store/slices/tenantSlice';
import {
  canSeeQuickbooksInvoice,
  canViewQuickbooksInvoiceAttachments,
  canEditInvoiceNumber as canEditInvoiceNumberPermission,
  canMarkJobQuickbooksInvoiced,
  canMarkJobFactored,
  canMarkJobPaid,
  canEditSentToClientOnJob,
  isAccounting,
  canDeleteJobAttachment,
} from '../../../../utils/permissions';
import { isImageFile, isVideoFile, isPreviewableMedia } from '../../../../utils/fileTypes';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

const DEFAULT_BRANDING = {
  name: '',
  formatted_address: '',
  email: '',
  phone: '',
  logo_download_url: null,
  logo_file_type: null,
};

const loadImageAsDataUrl = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch (err) {
      reject(err);
    }
  };
  img.onerror = reject;
  img.src = url;
});

// Format proposal item type for display (e.g. service_call -> "Service Call")
const formatItemType = (type) => {
  if (!type) return 'N/A';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Invoice # view/edit: labeled actions, adequate touch targets, used in accordion + detail. */
function InvoiceNumberInlineEditor({
  variant,
  proposal,
  editingInvoiceNumber,
  invoiceNumberValue,
  invoiceNumberError,
  invoiceNumberLoading,
  canEditInvoiceNumber,
  onStartEdit,
  onCancelEdit,
  onSave,
  onValueChange,
}) {
  const isEditing = editingInvoiceNumber === proposal.id;
  const canShowEdit = canEditInvoiceNumber && proposal.invoiced;
  const stopAccordionToggle = (e) => e.stopPropagation();

  if (isEditing) {
    const textField = (
      <TextField
        size="small"
        value={invoiceNumberValue}
        onChange={(e) => {
          onValueChange(e.target.value);
        }}
        placeholder="EOLAB - 00001"
        error={!!invoiceNumberError}
        helperText={invoiceNumberError || 'Letters, numbers, spaces, and . _ / - (max 50)'}
        disabled={invoiceNumberLoading}
        inputProps={{ maxLength: 50, 'aria-label': 'Invoice number' }}
        sx={
          variant === 'accordionSummary'
            ? {
                width: { xs: '100%', sm: 200 },
                '& .MuiInputBase-input': { fontSize: '0.875rem', fontWeight: 600 },
              }
            : {
                width: '100%',
                maxWidth: 280,
              }
        }
      />
    );

    const summarySafe = variant === 'accordionSummary';
    const actions = (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          {...(summarySafe
            ? { component: 'div', role: 'button', tabIndex: invoiceNumberLoading ? -1 : 0 }
            : { type: 'button' })}
          variant="contained"
          color="success"
          size="small"
          onClick={(e) => onSave(proposal.id, e)}
          disabled={invoiceNumberLoading}
          sx={{ textTransform: 'none', minHeight: 36, px: 2 }}
        >
          {invoiceNumberLoading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
        <Button
          {...(summarySafe
            ? { component: 'div', role: 'button', tabIndex: invoiceNumberLoading ? -1 : 0 }
            : { type: 'button' })}
          variant="outlined"
          size="small"
          onClick={onCancelEdit}
          disabled={invoiceNumberLoading}
          sx={{ textTransform: 'none', minHeight: 36, px: 2 }}
        >
          Cancel
        </Button>
      </Stack>
    );

    if (variant === 'invoiceDetail') {
      return (
        <Box
          onClick={stopAccordionToggle}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 1,
            width: '100%',
            maxWidth: 300,
            ml: 'auto',
          }}
        >
          <Typography variant="body2" component="div" sx={{ width: '100%', textAlign: 'right' }}>
            <strong>Invoice #:</strong>
          </Typography>
          {textField}
          {actions}
        </Box>
      );
    }

    return (
      <Box
        onClick={stopAccordionToggle}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 1.5,
          flex: 1,
          minWidth: 0,
        }}
      >
        {textField}
        {actions}
      </Box>
    );
  }

  if (variant === 'accordionSummary') {
    return (
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Invoice {proposal.invoice_number || 'N/A'}
        </Typography>
        {canShowEdit && (
          <Button
            component="div"
            role="button"
            tabIndex={0}
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={(e) => onStartEdit(proposal, e)}
            aria-label="Edit invoice number"
            sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, flexShrink: 0 }}
          >
            Edit number
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        flexWrap: 'wrap',
        rowGap: 1,
      }}
    >
      <Typography variant="body2" component="span">
        <strong>Invoice #:</strong>
      </Typography>
      <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
        {proposal.invoice_number || 'N/A'}
      </Typography>
      {canShowEdit && (
        <Button
          type="button"
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={(e) => onStartEdit(proposal, e)}
          aria-label="Edit invoice number"
          sx={{ textTransform: 'none', minHeight: 36 }}
        >
          Edit number
        </Button>
      )}
    </Box>
  );
}

const InvoiceTab = ({ 
  jobDetails, 
  proposals, 
  user, 
  onProposalsUpdate, 
  attachments = [],
  attachmentPreviewUrls = {},
  loadingAttachmentPreviews = {},
  onAttachmentPreview, 
  onAttachmentDownload,
  onAttachmentDelete,
  deletingAttachments = {},
  onToggleQuickbooksInvoiced,
  onToggleFactored,
  onTogglePaid,
  onToggleSentToClient,
  loadingQuickbooksInvoiced = false,
  loadingFactored = false,
  loadingPaid = false,
  loadingSentToClient = false,
  isJobLocked = false,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const selectedCompanyName = useSelector(getSelectedCompanyName);
  const isTabLocked = !!isJobLocked;
  const accountingMayEditLockedInvoice = isAccounting(user);

  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [brandingLoading, setBrandingLoading] = useState(true);
  
  // Filter to only invoiced proposals
  const invoicedProposals = proposals?.filter(p => p.invoiced) || [];
  
  // Accounting: all QB files. Admin: public QB only (view/preview/download).
  const canViewQuickbooksInvoice = canViewQuickbooksInvoiceAttachments(user);
  const canManageQuickbooksInvoice = canSeeQuickbooksInvoice(user);
  const quickbooksInvoiceAttachments = !canViewQuickbooksInvoice
    ? []
    : canManageQuickbooksInvoice
      ? attachments.filter((att) => att.category === 'quickbooks_invoice')
      : attachments.filter(
          (att) => att.category === 'quickbooks_invoice' && !att.is_private
        );
  
  // State to track expanded invoice (first one expanded by default)
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const hasInitialized = useRef(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDetails, setEmailDetails] = useState(null);
  
  // State for invoice number editing
  const [editingInvoiceNumber, setEditingInvoiceNumber] = useState(null); // proposal id being edited
  const [invoiceNumberValue, setInvoiceNumberValue] = useState('');
  const [invoiceNumberError, setInvoiceNumberError] = useState('');
  const [invoiceNumberLoading, setInvoiceNumberLoading] = useState(false);
  
  // Owner / accountant / GM; job lock does not block (mirrors API).
  const canEditInvoiceNumber =
    canEditInvoiceNumberPermission(user) && (!isTabLocked || accountingMayEditLockedInvoice);

  // Helper to get file icon based on file type
  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) {
      return <ImageIcon sx={{ fontSize: 32, color: '#4caf50' }} />;
    } else if (fileType === 'application/pdf') {
      return <PictureAsPdfIcon sx={{ fontSize: 32, color: '#f44336' }} />;
    } else if (fileType?.includes('word') || fileType?.includes('document')) {
      return <DescriptionIcon sx={{ fontSize: 32, color: '#2196f3' }} />;
    } else if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) {
      return <InsertDriveFileIcon sx={{ fontSize: 32, color: '#4caf50' }} />;
    } else {
      return <InsertDriveFileIcon sx={{ fontSize: 32, color: '#9e9e9e' }} />;
    }
  };
  
  // Set first invoice as expanded on mount or when invoices change (only once)
  useEffect(() => {
    if (invoicedProposals.length > 0 && !hasInitialized.current) {
      setExpandedInvoice(invoicedProposals[0].id);
      hasInitialized.current = true;
    }
  }, [invoicedProposals]);

  useEffect(() => {
    let cancelled = false;
    const fetchBranding = async () => {
      setBrandingLoading(true);
      const res = await GET(api.getCompanyInvoiceBranding);
      if (!cancelled) {
        if (!res.error && res.data?.branding) {
          setBranding({
            ...DEFAULT_BRANDING,
            ...res.data.branding,
            name: res.data.branding.name || selectedCompanyName || 'Company',
          });
        } else {
          setBranding({
            ...DEFAULT_BRANDING,
            name: selectedCompanyName || 'Company',
          });
        }
        setBrandingLoading(false);
      }
    };
    fetchBranding();
    return () => { cancelled = true; };
  }, [selectedCompanyName]);

  const companyInfo = {
    name: branding.name || selectedCompanyName || 'Company',
    address: branding.formatted_address || '',
    email: branding.email || '',
    phone: branding.phone || '',
  };

  const formatDate = (dateString) => formatWallClockDate(dateString);

  const getDueDate = (invoiceDate) => formatWallClockDate(addDaysToWallClockDate(invoiceDate, 30));

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const generatePDF = async (proposal) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;
    let leftY = margin;

    // Optional logo (left of header)
    if (branding.logo_download_url) {
      try {
        const dataUrl = await loadImageAsDataUrl(branding.logo_download_url);
        const format = branding.logo_file_type?.includes('png') ? 'PNG' : 'JPEG';
        doc.addImage(dataUrl, format, margin, leftY, 40, 20, undefined, 'FAST');
        leftY += 24;
      } catch (logoErr) {
        console.warn('Could not embed logo in PDF:', logoErr);
      }
    }

    // Invoice header
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', pageWidth - margin, yPos, { align: 'right' });
    yPos += 10;

    // Invoice details (right side)
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const invoiceDate = formatDate(proposal.invoiced_at);
    const dueDate = getDueDate(proposal.invoiced_at);
    
    doc.text(`Invoice #: ${proposal.invoice_number || 'N/A'}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 6;
    doc.text(`Date: ${invoiceDate}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 6;
    doc.text(`Due Date: ${dueDate}`, pageWidth - margin, yPos, { align: 'right' });

    // Company info (left side)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(companyInfo.name, margin, leftY);
    leftY += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (companyInfo.address) {
      doc.text(companyInfo.address, margin, leftY);
      leftY += 6;
    }
    if (companyInfo.email) {
      doc.text(`Contact Email: ${companyInfo.email}`, margin, leftY);
      leftY += 6;
    }
    if (companyInfo.phone) {
      doc.text(`Phone: ${companyInfo.phone}`, margin, leftY);
      leftY += 6;
    }
    yPos = Math.max(yPos, leftY) + 10;

    // Bill To section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Bill To:', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text(jobDetails?.client_name || 'N/A', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const clientAddress = `${jobDetails?.client_address || jobDetails?.address || ''}, ${jobDetails?.city || ''}, ${jobDetails?.state || ''} ${jobDetails?.client_zip_code || jobDetails?.zip_code || ''}`.trim();
    doc.text(clientAddress, margin, yPos);
    yPos += 6;
    if (jobDetails?.client_email) {
      doc.text(`Contact Email: ${jobDetails.client_email}`, margin, yPos);
      yPos += 6;
    }
    if (jobDetails?.client_phone) {
      doc.text(`Phone Number: ${jobDetails.client_phone}`, margin, yPos);
      yPos += 6;
    }
    yPos += 10;

    // Project Details
    doc.setFont('helvetica', 'bold');
    doc.text('Project Details:', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text(jobDetails?.description || 'N/A', margin, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    const proposalDescription = `Description: ${proposal.proposal_text || 'N/A'}`;
    const maxWidth = pageWidth - 2 * margin;
    const splitProposalDescription = doc.splitTextToSize(proposalDescription, maxWidth);
    doc.text(splitProposalDescription, margin, yPos);
    yPos += 6 * splitProposalDescription.length;
    doc.text(`Location: ${clientAddress}`, margin, yPos);
    yPos += 15;

    // Line items table
    // Build table body from proposal items array
    const items = proposal.items || [];
    const tableBody = items.map(item => [
      item.description || 'N/A',
      formatItemType(item.type),
      (item.quantity || 0).toString(),
      formatCurrency(item.unit_price || 0),
      formatCurrency(item.price || (item.quantity * item.unit_price) || 0)
    ]);

    // If no items, show legacy single item (backward compatibility)
    if (tableBody.length === 0) {
      const quantity = proposal.quantity || 0;
      const rate = proposal.unit_price || 0;
      const amount = proposal.price || (quantity * rate);
      tableBody.push([
        proposal.proposal_text || 'N/A',
        formatItemType(proposal.type),
        quantity.toString(),
        formatCurrency(rate),
        formatCurrency(amount)
      ]);
    }

    autoTable(doc, {
      startY: yPos,
      head: [['Description', 'Type', 'Qty', 'Rate', 'Amount']],
      body: tableBody,
      theme: 'grid',
      headStyles: { 
        fillColor: [245, 245, 245], 
        textColor: [0, 0, 0], 
        fontStyle: 'bold',
        0: { cellWidth: 45 }
      },
      styles: { fontSize: 10, cellPadding: 5 },
      columnStyles: {
        0: { cellWidth: 45, overflow: 'linebreak', cellPadding: { left: 3, right: 3, top: 2, bottom: 2 } },
        1: { cellWidth: 35, halign: 'center' },
        2: { cellWidth: 25, halign: 'center' },
        3: { cellWidth: 35, halign: 'right' },
        4: { cellWidth: 35, halign: 'right' }
      },
      margin: { left: margin, right: margin }
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Totals
    const totalsX = pageWidth - margin - 40;
    const subtotal = proposal.price || 0;
    const total = proposal.price || 0;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Subtotal:', totalsX, yPos, { align: 'right' });
    doc.text(formatCurrency(subtotal), pageWidth - margin, yPos, { align: 'right' });
    yPos += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Total:', totalsX, yPos, { align: 'right' });
    doc.text(formatCurrency(total), pageWidth - margin, yPos, { align: 'right' });
    yPos += 20;

    // Payment terms
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const paymentTerms = `Payment is due within 30 days. Please make checks payable to ${companyInfo.name}.`;
    doc.text(paymentTerms, margin, yPos, { maxWidth: pageWidth - 2 * margin });
    yPos += 8;
    doc.text('Thank you for your business!', margin, yPos);

    // Download PDF
    doc.save(`Invoice-${proposal.invoice_number || proposal.id}.pdf`);
  };

  const handleEmailInvoice = (proposal, e) => {
    if (isTabLocked && !accountingMayEditLockedInvoice) return;
    e?.stopPropagation?.();
    e?.preventDefault?.();
    
    const clientEmail = jobDetails?.client_email || '';
    
    if (!clientEmail) {
      alert('Client email not available. Please add email to client information.');
      return;
    }

    const invoiceDate = formatDate(proposal.invoiced_at);
    const dueDate = getDueDate(proposal.invoiced_at);
    const subject = `Invoice ${proposal.invoice_number || ''} - ${jobDetails?.client_name || ''}`;
    const body = `Dear ${jobDetails?.client_name || 'Client'},

Please find attached invoice ${proposal.invoice_number || ''} for the project: ${jobDetails?.description || ''}.

Invoice Date: ${invoiceDate}
Due Date: ${dueDate}
Amount: ${formatCurrency(proposal.price)}

Thank you for your business!

Best regards,
${companyInfo.name}`;

    // Store email details for dialog
    setEmailDetails({
      to: clientEmail,
      subject: subject,
      body: body
    });

    // Show dialog immediately with email details
    setEmailDialogOpen(true);
  };

  const handleOpenEmailClient = () => {
    if (!emailDetails) return;
    
    const mailtoLink = `mailto:${emailDetails.to}?subject=${encodeURIComponent(emailDetails.subject)}&body=${encodeURIComponent(emailDetails.body)}`;
    window.location.href = mailtoLink;
  };

  const handleCopyEmailDetails = async () => {
    if (!emailDetails) return;
    
    const emailText = `To: ${emailDetails.to}\nSubject: ${emailDetails.subject}\n\n${emailDetails.body}`;
    
    try {
      await navigator.clipboard.writeText(emailText);
      alert('Email details copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy to clipboard. Please manually copy the email details.');
    }
  };

  // Start editing invoice number
  const handleStartEditInvoiceNumber = (proposal, e) => {
    if (isTabLocked && !accountingMayEditLockedInvoice) return;
    e?.stopPropagation?.();
    setEditingInvoiceNumber(proposal.id);
    setInvoiceNumberValue(proposal.invoice_number || '');
    setInvoiceNumberError('');
  };

  // Cancel editing invoice number
  const handleCancelEditInvoiceNumber = (e) => {
    e?.stopPropagation?.();
    setEditingInvoiceNumber(null);
    setInvoiceNumberValue('');
    setInvoiceNumberError('');
  };

  // Save invoice number
  const handleSaveInvoiceNumber = async (proposalId, e) => {
    if (isTabLocked && !accountingMayEditLockedInvoice) return;
    e?.stopPropagation?.();
    
    const trimmed = invoiceNumberValue.trim();
    if (!/^[A-Za-z0-9._/ -]{1,50}$/.test(trimmed)) {
      setInvoiceNumberError(
        'Use 1–50 characters: letters, numbers, spaces, and . _ / - only'
      );
      return;
    }

    setInvoiceNumberLoading(true);
    setInvoiceNumberError('');

    try {
      const { status, message } = await PATCH(
        `${api.getJobById}/${jobDetails.id}/proposals/${proposalId}/invoice-number`,
        {},
        { invoice_number: trimmed }
      );

      if (status === 200) {
        // Success - refresh proposals and clear editing state
        if (onProposalsUpdate) {
          await onProposalsUpdate();
        }
        setEditingInvoiceNumber(null);
        setInvoiceNumberValue('');
      } else {
        // Show error message from backend
        setInvoiceNumberError(message || 'Failed to update invoice number');
      }
    } catch (error) {
      console.error('Failed to update invoice number:', error);
      setInvoiceNumberError(error.message || 'Failed to update invoice number');
    } finally {
      setInvoiceNumberLoading(false);
    }
  };

  const financialToggleLockedOut =
    isTabLocked && !accountingMayEditLockedInvoice;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        {invoicedProposals.length > 0 ? 'Invoices' : 'Invoice'}
      </Typography>

      {/* Financial Status Toggles */}
      <Box sx={{ 
        display: 'flex', 
        gap: 4, 
        mb: 2, 
        p: 2, 
        backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#f5f5f5', 
        borderRadius: '8px',
        border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* QuickBooks Invoiced Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            QB Invoiced
          </Typography>
          {loadingQuickbooksInvoiced ? (
            <CircularProgress size={20} />
          ) : (
            <Switch
              checked={jobDetails?.quickbooks_invoiced || false}
              onChange={onToggleQuickbooksInvoiced}
              disabled={financialToggleLockedOut || !canMarkJobQuickbooksInvoiced(user)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4caf50',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#4caf50',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                  backgroundColor: '#4caf50',
                },
                '& .MuiSwitch-track': {
                  backgroundColor: isDarkMode ? '#475569' : '#ccc',
                },
              }}
            />
          )}
          <Typography variant="body2" sx={{ color: jobDetails?.quickbooks_invoiced ? '#4caf50' : 'text.secondary', fontWeight: 500, minWidth: 30 }}>
            {jobDetails?.quickbooks_invoiced ? 'Yes' : 'No'}
          </Typography>
        </Box>

        {/* Sent to Client Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            Sent to client
          </Typography>
          {loadingSentToClient ? (
            <CircularProgress size={20} />
          ) : (
            <Switch
              checked={jobDetails?.sent_to_client || false}
              onChange={onToggleSentToClient || (() => {})}
              disabled={!canEditSentToClientOnJob(user, isTabLocked) || !onToggleSentToClient}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4caf50',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#4caf50',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                  backgroundColor: '#4caf50',
                },
                '& .MuiSwitch-track': {
                  backgroundColor: isDarkMode ? '#475569' : '#ccc',
                },
              }}
            />
          )}
          <Typography variant="body2" sx={{ color: jobDetails?.sent_to_client ? '#4caf50' : 'text.secondary', fontWeight: 500, minWidth: 30 }}>
            {jobDetails?.sent_to_client ? 'Yes' : 'No'}
          </Typography>
        </Box>

        {/* Paid Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            Paid
          </Typography>
          {loadingPaid ? (
            <CircularProgress size={20} />
          ) : (
            <Switch
              checked={jobDetails?.paid || false}
              onChange={onTogglePaid || (() => {})}
              disabled={financialToggleLockedOut || !canMarkJobPaid(user) || !onTogglePaid}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4caf50',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#4caf50',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                  backgroundColor: '#4caf50',
                },
                '& .MuiSwitch-track': {
                  backgroundColor: isDarkMode ? '#475569' : '#ccc',
                },
              }}
            />
          )}
          <Typography variant="body2" sx={{ color: jobDetails?.paid ? '#4caf50' : 'text.secondary', fontWeight: 500, minWidth: 30 }}>
            {jobDetails?.paid ? 'Yes' : 'No'}
          </Typography>
        </Box>

        {/* Factored Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            Factored
          </Typography>
          {loadingFactored ? (
            <CircularProgress size={20} />
          ) : (
            <Switch
              checked={jobDetails?.factored || false}
              onChange={onToggleFactored}
              disabled={financialToggleLockedOut || !canMarkJobFactored(user)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4caf50',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#4caf50',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                  backgroundColor: '#4caf50',
                },
                '& .MuiSwitch-track': {
                  backgroundColor: isDarkMode ? '#475569' : '#ccc',
                },
              }}
            />
          )}
          <Typography variant="body2" sx={{ color: jobDetails?.factored ? '#4caf50' : 'text.secondary', fontWeight: 500, minWidth: 30 }}>
            {jobDetails?.factored ? 'Yes' : 'No'}
          </Typography>
        </Box>
      </Box>

      {/* Quickbooks Invoice Attachments Section - Always displayed for authorized users */}
      {canViewQuickbooksInvoice && quickbooksInvoiceAttachments.length > 0 && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 175, 80, 0.05)', 
          borderRadius: '8px',
          border: isDarkMode ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(76, 175, 80, 0.2)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AttachFileIcon sx={{ color: '#4caf50', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Quickbooks Invoice Attachments ({quickbooksInvoiceAttachments.length})
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2 
          }}>
            {quickbooksInvoiceAttachments.map((attachment) => (
              <Box
                key={attachment.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? 'background.paper' : '#ffffff',
                  '&:hover': { 
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5' 
                  }
                }}
              >
                <Box 
                  onClick={() => onAttachmentPreview?.(attachment)}
                  role="button"
                  sx={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: 56,
                    height: 56,
                    borderRadius: '6px',
                    overflow: 'hidden',
                    position: 'relative',
                    backgroundColor: isDarkMode ? 'action.hover' : 'grey.100',
                    '&:hover': { opacity: 0.85 }
                  }}
                >
                  {isImageFile(attachment.file_type) && attachmentPreviewUrls[attachment.id] ? (
                    <img
                      src={attachmentPreviewUrls[attachment.id]}
                      alt={attachment.file_name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : isVideoFile(attachment.file_type) && attachmentPreviewUrls[attachment.id] ? (
                    <>
                      <video
                        src={attachmentPreviewUrls[attachment.id]}
                        muted
                        playsInline
                        preload="metadata"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          backgroundColor: '#000',
                        }}
                      />
                      <PlayCircleOutlineIcon
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: 'rgba(255,255,255,0.95)',
                          pointerEvents: 'none',
                          fontSize: 28,
                          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.65))',
                        }}
                      />
                    </>
                  ) : isPreviewableMedia(attachment.file_type) && loadingAttachmentPreviews[attachment.id] ? (
                    <CircularProgress size={18} />
                  ) : (
                    getFileIcon(attachment.file_type)
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Typography 
                    variant="body2" 
                    title={attachment.file_name}
                    sx={{ 
                      fontWeight: 500, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      color: 'text.primary'
                    }}
                  >
                    {attachment.file_name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {(attachment.file_size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => onAttachmentPreview?.(attachment)}
                    sx={{ color: '#1976d2' }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onAttachmentDownload?.(attachment)}
                    sx={{ color: '#1976d2' }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  {canDeleteJobAttachment(user, attachment, jobDetails, isTabLocked) && (
                    <IconButton
                      size="small"
                      onClick={() => onAttachmentDelete?.(attachment)}
                      disabled={!!deletingAttachments[attachment.id]}
                      sx={{
                        color: '#d32f2f',
                        '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
                      }}
                      aria-label={`Delete ${attachment.file_name}`}
                    >
                      {deletingAttachments[attachment.id] ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DeleteIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* No invoices message */}
      {(!invoicedProposals || invoicedProposals.length === 0) && (
        <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          No invoice available for this job.
        </Typography>
      )}

      {invoicedProposals.map((proposal) => {
        const invoiceDate = formatDate(proposal.invoiced_at);
        const dueDate = getDueDate(proposal.invoiced_at);

        return (
          <Accordion
            key={proposal.id}
            expanded={expandedInvoice === proposal.id}
            onChange={(e, isExpanded) => setExpandedInvoice(isExpanded ? proposal.id : null)}
            sx={{
              borderRadius: '12px !important',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:before': { display: 'none' },
              mb: 2
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 3,
                py: 2,
                '& .MuiAccordionSummary-content': {
                  my: 1
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                flexWrap: 'wrap',
                gap: 1.5,
                width: '100%',
                pr: 2,
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                  flexWrap: 'wrap',
                  rowGap: 1,
                }}>
                  <BuildIcon sx={{ color: 'text.secondary', fontSize: 24, flexShrink: 0 }} />
                  <InvoiceNumberInlineEditor
                    variant="accordionSummary"
                    proposal={proposal}
                    editingInvoiceNumber={editingInvoiceNumber}
                    invoiceNumberValue={invoiceNumberValue}
                    invoiceNumberError={invoiceNumberError}
                    invoiceNumberLoading={invoiceNumberLoading}
                    canEditInvoiceNumber={canEditInvoiceNumber}
                    onStartEdit={handleStartEditInvoiceNumber}
                    onCancelEdit={handleCancelEditInvoiceNumber}
                    onSave={handleSaveInvoiceNumber}
                    onValueChange={(v) => {
                      setInvoiceNumberValue(v);
                      setInvoiceNumberError('');
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary', ml: { xs: 0, sm: 2 }, flexShrink: 0 }}>
                    {invoiceDate} • Due: {dueDate}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Coming soon">
                    <span>
                      <Button
                        component="div"
                        role="button"
                        tabIndex={-1}
                        variant="outlined"
                        size="small"
                        startIcon={<PictureAsPdfIcon />}
                        disabled
                        sx={{
                          textTransform: 'none',
                          color: '#d32f2f',
                          borderColor: '#d32f2f',
                          '&.Mui-disabled': {
                            color: '#d32f2f',
                            borderColor: '#d32f2f',
                            opacity: 0.5,
                          },
                        }}
                      >
                        Download PDF
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    component="div"
                    role="button"
                    tabIndex={isTabLocked && !accountingMayEditLockedInvoice ? -1 : 0}
                    variant="contained"
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={(e) => handleEmailInvoice(proposal, e)}
                    disabled={isTabLocked && !accountingMayEditLockedInvoice}
                    sx={{
                      bgcolor: '#1a1a1a',
                      '&:hover': { bgcolor: '#333' },
                      textTransform: 'none'
                    }}
                  >
                    Send Email
                  </Button>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              {/* Invoice Content */}
              <Box sx={{ 
                border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0', 
                borderRadius: '8px', 
                p: 3, 
                backgroundColor: isDarkMode ? 'background.paper' : '#ffffff' 
              }}>
              {/* Top Section: Invoice #, Date, Due Date */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                    INVOICE
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', minWidth: 0, maxWidth: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5, width: '100%' }}>
                    <InvoiceNumberInlineEditor
                      variant="invoiceDetail"
                      proposal={proposal}
                      editingInvoiceNumber={editingInvoiceNumber}
                      invoiceNumberValue={invoiceNumberValue}
                      invoiceNumberError={invoiceNumberError}
                      invoiceNumberLoading={invoiceNumberLoading}
                      canEditInvoiceNumber={canEditInvoiceNumber}
                      onStartEdit={handleStartEditInvoiceNumber}
                      onCancelEdit={handleCancelEditInvoiceNumber}
                      onSave={handleSaveInvoiceNumber}
                      onValueChange={(v) => {
                        setInvoiceNumberValue(v);
                        setInvoiceNumberError('');
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Date:</strong> {invoiceDate}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Due Date:</strong> {dueDate}
                  </Typography>
                </Box>
              </Box>

              {/* Company Info */}
              <Box sx={{ mb: 3 }}>
                {brandingLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    {branding.logo_download_url && (
                      <Box sx={{ mb: 1.5 }}>
                        <Box
                          component="img"
                          src={branding.logo_download_url}
                          alt="Company logo"
                          sx={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain' }}
                        />
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BuildIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {companyInfo.name}
                      </Typography>
                    </Box>
                    {companyInfo.address && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {companyInfo.address}
                      </Typography>
                    )}
                    {companyInfo.email && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Contact Email: {companyInfo.email}
                      </Typography>
                    )}
                    {companyInfo.phone && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Phone: {companyInfo.phone}
                      </Typography>
                    )}
                  </>
                )}
              </Box>

              {/* Bill To */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Bill To:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {jobDetails?.client_name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {jobDetails?.client_address || jobDetails?.address || ''}, {jobDetails?.city || ''}, {jobDetails?.state || ''} {jobDetails?.client_zip_code || jobDetails?.zip_code || ''}
                </Typography>
                {jobDetails?.client_email && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Contact Email: {jobDetails.client_email}
                  </Typography>
                )}
                {jobDetails?.client_phone && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Phone Number: {jobDetails.client_phone}
                  </Typography>
                )}
              </Box>

              {/* Project Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Project Details:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {jobDetails?.description || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  Description: {proposal.proposal_text || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Location: {jobDetails?.client_address || jobDetails?.address || ''}, {jobDetails?.city || ''}, {jobDetails?.state || ''} {jobDetails?.client_zip_code || jobDetails?.zip_code || ''}
                </Typography>
              </Box>

              {/* Divider */}
              <Box sx={{ borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0', my: 3 }} />

              {/* Line Items Table */}
              <Table sx={{ 
                mb: 3,
                backgroundColor: isDarkMode ? 'background.paper' : 'transparent',
                tableLayout: 'auto',
                width: '100%'
              }}>
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                  }}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', minWidth: 75, paddingRight: 2 }}>Description</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>Type</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>Quantity</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>Rate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(proposal.items && proposal.items.length > 0) ? (
                    proposal.items.map((item, index) => (
                      <TableRow key={index} sx={{ 
                        '&:hover': {
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                        }
                      }}>
                        <TableCell sx={{ color: 'text.primary', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: 150 }}>{item.description || 'N/A'}</TableCell>
                        <TableCell align="center" sx={{ color: 'text.primary' }}>
                          {formatItemType(item.type)}
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'text.primary' }}>{item.quantity || 0}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{formatCurrency(item.unit_price || 0)}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.primary' }}>{formatCurrency(item.price || (item.quantity * item.unit_price) || 0)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    // Backward compatibility: show single item if no items array
                    <TableRow sx={{ 
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'
                      }
                    }}>
                      <TableCell sx={{ color: 'text.primary', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: 150 }}>{proposal.proposal_text || 'N/A'}</TableCell>
                      <TableCell align="center" sx={{ color: 'text.primary' }}>
                        {formatItemType(proposal.type)}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'text.primary' }}>{proposal.quantity || 0}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.primary' }}>{formatCurrency(proposal.unit_price || 0)}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.primary' }}>{formatCurrency(proposal.price || 0)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Totals */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Box sx={{ minWidth: 200 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1">Subtotal:</Typography>
                    <Typography variant="body1">{formatCurrency(proposal.price || 0)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Total:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{formatCurrency(proposal.price || 0)}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Payment Terms */}
              <Box sx={{ borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0', pt: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Payment is due within 30 days. Please make checks payable to {companyInfo.name}.
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Thank you for your business!
                </Typography>
              </Box>
            </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Email Details Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Email Invoice Details
          </Typography>
          <IconButton
            onClick={() => setEmailDialogOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {emailDetails && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                  To:
                </Typography>
                <Typography variant="body1">
                  {emailDetails.to}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                  Subject:
                </Typography>
                <Typography variant="body1">
                  {emailDetails.subject}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                  Body:
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                  {emailDetails.body}
                </Typography>
              </Box>
              <Box sx={{ mt: 2, p: 2, bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  If your email client didn't open automatically, you can copy the details above and paste them into your email client manually.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setEmailDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
          <Button
            onClick={handleCopyEmailDetails}
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            disabled={isTabLocked && !accountingMayEditLockedInvoice}
            sx={{ textTransform: 'none' }}
          >
            Copy Details
          </Button>
          <Button
            onClick={handleOpenEmailClient}
            variant="contained"
            startIcon={<EmailIcon />}
            disabled={isTabLocked && !accountingMayEditLockedInvoice}
            sx={{
              bgcolor: '#1a1a1a',
              '&:hover': { bgcolor: '#333' },
              textTransform: 'none'
            }}
          >
            Open Email Client
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceTab;
